// @flow
import React, {
    useCallback,
    useEffect,
    useRef,
    useMemo,
    useState,
} from "react";
import Icons from "@hs/icons";
import Button from "react-bootstrap/lib/Button";
import NoteInput, { getNoteType } from "components/NoteInput";
import {
    type ActivityDTO,
    type ActivityFieldDTO,
    ActivityType,
    ItemNoteType,
    ItemNoteTypeIcon,
    RetentionType,
} from "data/types";
import Avatar from "components/User/Avatar";
import Table from "react-bootstrap/lib/Table";
import loc from "i18next";
import Badge from "react-bootstrap/lib/Badge";
import styles from "./Activity.module.css";
import RetentionInfo from "./RetentionInfo";
import { ActivityAction } from "./Actions";
import ActionsMenu from "components/ActionsMenu";
import Attributes from "components/Attributes";
import { LockInfoOn, UnlockedOn } from "components/LockInfo";
import { getClassNames } from "data/utils";

type Props = {
    /** activity to render */
    activity: ActivityDTO,
    /** current user's id */
    userId: number,
    /** whether to show activities details
     * @default {boolean} false
     */
    showDetails?: boolean,
    /** whether to show the Actions menu
     * @default {boolean} false
     */
    showActions?: boolean,
    /** whether to render in edit state
     * @default {boolean} false
     */
    isEdit: boolean,
    /** whether to render in reply state
     * @default {boolean} false
     */
    isReply: boolean,
    /** set items edit state
     * @param {boolean} isEdit new isEdit state
     */
    isHighlighted: boolean,
    /** callback when user chooses an action
     * @param {boolean} false
     */
    isSelected?: boolean,
    /** whether this activity is the last version available
     * @default {boolean} false
     */
    isLastVersion?: boolean,
    /** callback when user chooses an action
     * @param {string} actionId action identifier
     * @param {?any} payload optional payload
     */
    onAction: (actionId: string, payload: ?any) => void,
    /** what action menu to display
     * @default {string} docactivity_context
     */
    actionMenu: string,
    /** whether Activity is read-only */
    isReadOnly?: boolean,
};

const getCommentIconString = (activity) => {
    if (
        activity?.extras?.$type == null ||
        !Object.prototype.hasOwnProperty.call(
            ItemNoteTypeIcon,
            activity.extras.$type
        )
    )
        return ItemNoteTypeIcon[ItemNoteType.Default];
    return ItemNoteTypeIcon[activity.extras.$type];
};

/** Renders a list of activities */
const Activity = ({
    activity,
    className,
    userId,
    showDetails = false,
    showActions = false,
    isEdit,
    isReply,
    isHighlighted,
    isSelected = false,
    isLastVersion,
    onAction,
    actionMenu = "docactivity_context",
    isReadOnly,
}: Props) => {
    const renderWhatIcon = useCallback(() => {
        switch (activity.what) {
            case ActivityType.create:
                return <Icons.Library name="plus" />;
            case ActivityType.modifyContent:
            case ActivityType.modifyField:
                return <Icons.Library name="pencil" />;
            case ActivityType.delete:
                return <Icons.Library name="trash-can" />;
            case ActivityType.comment:
                return activity.parentId > 0 ? (
                    <Icons.Library
                        className="reply"
                        name={getCommentIconString(activity)}
                    />
                ) : (
                    <Icons.Library
                        className="comment"
                        onClick={
                            /*istanbul ignore next */ () =>
                                onAction("filter_note")
                        }
                        name={getCommentIconString(activity)}
                        title={loc.t("activity:note.filter")}
                    />
                );
            case ActivityType.system:
                return (
                    <Icons.Library name={activity.extras?.$icon ?? "gear"} />
                );
            default:
                return null;
        }
    }, [activity, onAction]);

    const renderWhat = useCallback((): string => {
        switch (activity.what) {
            case ActivityType.create:
            case ActivityType.modifyContent:
            case ActivityType.modifyField:
            case ActivityType.comment:
            case ActivityType.delete:
                return loc.t(`common:act.changes_${activity.what}`, {
                    date: activity.on,
                });
            case ActivityType.system:
                return activity.on;
            case ActivityType.locked:
                return <LockInfoOn date={activity.on} />;
            case ActivityType.unlocked:
                return <UnlockedOn date={activity.on} />;
            default:
                return "unknown what=" + activity.what;
        }
    }, [activity]);

    const renderChange = useCallback(
        (change: ActivityFieldDTO, index: number) => (
            <tr key={`ac-${index}`}>
                <td>{change.caption}</td>
                <td>{change.newVal}</td>
                <td>{change.oldVal}</td>
            </tr>
        ),
        []
    );

    const renderChanges = useCallback(
        () => (
            <div className={styles.container}>
                <Table condensed className={styles.table}>
                    <thead>
                        <tr>
                            <th>&nbsp;</th>
                            <th>{loc.t("common:act.changes_newVal")}</th>
                            <th>{loc.t("common:act.changes_oldVal")}</th>
                        </tr>
                    </thead>
                    <tbody className={styles.tbody}>
                        {activity.changes.map((change, index) =>
                            renderChange(change, index)
                        )}
                    </tbody>
                </Table>
            </div>
        ),
        [activity, renderChange]
    );

    const activityRef = useRef(null);
    useEffect(() => {
        /* istanbul ignore else */
        if (isHighlighted && activityRef.current) {
            activityRef.current.scrollIntoView({
                block: "center",
                inline: "center",
            });
        }
    }, [isHighlighted]);

    const resetActivity = useCallback(() => {
        onAction(ActivityAction.Edit, false);
        onAction(ActivityAction.Reply, false);
    }, [onAction]);

    const handleUpdateEnter: (activity: ActivityDTO) => Promise<boolean> =
        useCallback(
            (activity: ActivityDTO) =>
                onAction(ActivityAction.Update, activity).then(
                    (result) => result && onAction(ActivityAction.Edit, false)
                ),
            [onAction]
        );

    const handleReplyEnter: (activity: ActivityDTO) => Promise<boolean> =
        useCallback(
            (activity: ActivityDTO) =>
                onAction(ActivityAction.Add, activity).then(
                    (result) => result && onAction(ActivityAction.Reply, false)
                ),
            [onAction]
        );

    const handleAbort: (e: SyntheticMouseEvent<*>) => void = useCallback(
        (e) => e.stopPropagation() || resetActivity(),
        [resetActivity]
    );

    const emptyReply: ActivityDTO = useMemo(
        () => ({
            parentId: activity.id,
            what: ActivityType.comment,
            extras: null,
            text: "",
        }),
        [activity.id]
    );

    const [edit, setEdit] = useState(null);
    const [reply, setReply] = useState(null);

    const renderText = useCallback(() => {
        switch (activity.what) {
            case ActivityType.create:
            case ActivityType.modifyContent:
            case ActivityType.modifyField:
                return activity.changes?.length ? renderChanges() : null;
            case ActivityType.system:
                return (
                    <>
                        {activity.text && (
                            <p
                                className={getClassNames(
                                    styles.text,
                                    activity.extras?.$color && styles.color
                                )}
                                style={{
                                    color: activity.extras?.$color ?? "initial",
                                }}
                            >
                                {activity.text}
                            </p>
                        )}
                        {activity.extras?.$attributes?.length && (
                            <Attributes
                                className={styles.attributes}
                                attributes={activity.extras?.$attributes}
                            />
                        )}
                    </>
                );
            case ActivityType.comment:
                return isEdit ? (
                    <NoteInput
                        activity={activity}
                        onEnter={handleUpdateEnter}
                        onAbort={handleAbort}
                    />
                ) : (
                    <p className={styles.text}>{activity.text}</p>
                );
            case ActivityType.locked:
            case ActivityType.unlocked:
                return null;
            default:
                return "unknown what=" + activity.what;
        }
    }, [activity, handleUpdateEnter, handleAbort, isEdit, renderChanges]);

    const onShouldBeVisible = (action: { id: string }): boolean =>
        !(
            isLastVersion === true &&
            ["sep", "restore", "delete"].includes(action.id)
        );

    return (
        <>
            <div
                ref={activityRef}
                className={getClassNames(
                    className,
                    styles.row,
                    isSelected ? "selected" : "",
                    isHighlighted ? "highlighted" : ""
                )}
                onClick={
                    /* istanbul ignore next */ () =>
                        (!isEdit || (isEdit && !isHighlighted)) &&
                        activity.parentId === 0 &&
                        onAction(ActivityAction.Highlight)
                }
            >
                <header className={styles.header}>
                    <Avatar
                        id={activity.userId}
                        className={getClassNames("round", styles.avatar)}
                    />
                    <span>{activity.userName}</span>
                    <span className={styles.icon}>{renderWhatIcon()}</span>
                    {showActions &&
                        [
                            ActivityType.create,
                            ActivityType.modifyContent,
                            ActivityType.modifyField,
                        ].includes(activity.what) && (
                            <ActionsMenu
                                toggleComponent={
                                    // <Icons.Library name="ellipsis-h" />
                                    <Badge className={styles.version}>
                                        {loc.t("common:version.text", {
                                            version: activity.id,
                                        })}
                                        <span className="caret" />
                                    </Badge>
                                }
                                name={actionMenu}
                                className={styles.actions}
                                onAction={onAction}
                                onShouldBeVisible={onShouldBeVisible}
                            />
                        )}
                    {activity.what === ActivityType.comment && (
                        <span className={styles.actions}>
                            {!isEdit &&
                                !isReply &&
                                !isReadOnly &&
                                activity.userId === userId && (
                                    <Button
                                        bsStyle="link"
                                        aria-label="edit"
                                        title={loc.t(
                                            `activity:${getNoteType(
                                                activity
                                            )}.edit.label`
                                        )}
                                        bsSize="xsmall"
                                        onClick={
                                            /* istanbul ignore next */ (e) =>
                                                (isHighlighted &&
                                                    e.stopPropagation()) ||
                                                onAction(
                                                    ActivityAction.Edit,
                                                    true
                                                )
                                        }
                                    >
                                        <Icons.Library name="pencil" />
                                    </Button>
                                )}
                            {!isEdit &&
                                !isReply &&
                                !isReadOnly &&
                                activity.parentId === 0 && (
                                    <Button
                                        bsStyle="link"
                                        aria-label="reply"
                                        title={loc.t(
                                            `activity:${getNoteType(
                                                activity
                                            )}.reply.label`
                                        )}
                                        bsSize="xsmall"
                                        onClick={
                                            /* istanbul ignore next */ (e) =>
                                                (isHighlighted &&
                                                    e.stopPropagation()) ||
                                                onAction(
                                                    ActivityAction.Reply,
                                                    true
                                                )
                                        }
                                    >
                                        <Icons.Library name="reply" />
                                    </Button>
                                )}
                            {isEdit && (
                                <Button
                                    bsStyle="link"
                                    aria-label="delete"
                                    title={loc.t(
                                        `activity:${getNoteType(
                                            activity
                                        )}.delete.label`
                                    )}
                                    bsSize="xsmall"
                                    onClick={
                                        /* istanbul ignore next */ (e) =>
                                            (isHighlighted &&
                                                e.stopPropagation()) ||
                                            onAction(
                                                ActivityAction.Delete,
                                                activity
                                            )
                                    }
                                >
                                    <Icons.Library name="trash-can" />
                                </Button>
                            )}
                        </span>
                    )}

                    <div className={styles.what}>{renderWhat()}</div>
                    {activity.retention != null &&
                        activity.retention.type !== RetentionType.Default && (
                            <RetentionInfo
                                info={activity.retention}
                                className={styles.retention}
                            />
                        )}
                </header>
                <article
                    className={getClassNames(
                        styles.article,
                        showDetails ||
                            [
                                ActivityType.comment,
                                ActivityType.system,
                            ].includes(activity.what)
                            ? ""
                            : "hidden"
                    )}
                >
                    {renderText(activity)}
                </article>
            </div>
            {activity.replies?.length > 0 &&
                activity.replies.map((item, index) => {
                    const handleAction = (actionId: string, payload: any) => {
                        switch (actionId) {
                            case ActivityAction.Edit:
                                resetActivity();
                                setEdit(payload ? item.id : null);
                                break;
                            case ActivityAction.Reply:
                                resetActivity();
                                setReply(payload ? item.id : null);
                                break;
                            case ActivityAction.Delete:
                                return onAction(actionId, payload).then(
                                    (success) => {
                                        setEdit(null);
                                        setReply(null);
                                    }
                                );
                            default:
                                return onAction(actionId, payload);
                        }
                    };
                    return (
                        <Activity
                            key={`reply-${activity.id}-${item.id}`}
                            className={styles.reply}
                            activity={item}
                            userId={userId}
                            onAction={handleAction}
                            isEdit={
                                item.what === ActivityType.comment &&
                                edit === item.id
                            }
                            isReply={
                                item.what === ActivityType.comment &&
                                reply === item.id
                            }
                            isReadOnly={isReadOnly}
                        />
                    );
                })}
            {isReply && (
                <NoteInput
                    className={styles.replyInput}
                    userId={userId}
                    activity={emptyReply}
                    onEnter={handleReplyEnter}
                    onAbort={handleAbort}
                    autoFocus={true}
                />
            )}
        </>
    );
};
Activity.displayName = "Activity";
export default Activity;
